// ────────────────────────────────────────────────────────────────────────
// MultiSelectHandle — 多选框右侧的拖拽加号
// ────────────────────────────────────────────────────────────────────────
// 显示条件：选中 ≥2 个普通节点
// 位置：多选框右侧 +30px，垂直居中
// 功能：拖拽发起多源连接，松手后连线由 EditorPage 层级渲染（与单选逻辑一致）

import React, { useState } from 'react';
import { useViewport, useReactFlow, useStore } from 'reactflow';
import { Plus } from 'lucide-react';
import * as C from '../constants';

const MultiSelectHandle = ({ selectedNodes, onMultiSelectConnectStart, isConnecting, hasMenu }) => {
  const viewport = useViewport();
  const [isHovering, setIsHovering] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPos, setDragPos] = useState(null);

  // 群组节点自身和已在群组中的节点都不参与
  const validNodes = selectedNodes?.filter(n => n.type !== 'group' && !n.data?.groupId) || [];
  if (validNodes.length < 2 || !onMultiSelectConnectStart) return null;

  // ── 计算多选框包围盒（flow 坐标） ──
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  validNodes.forEach(node => {
    const x = node.position.x;
    const y = node.position.y;
    const w = node.width || node.measured?.width || node.data?.customSize?.width || 200;
    const h = node.height || node.measured?.height || node.data?.customSize?.height || 150;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x + w > maxX) maxX = x + w;
    if (y + h > maxY) maxY = y + h;
  });
  if (minX === Infinity) return null;

  // flow → 屏幕坐标
  const { x: vpX, y: vpY, zoom } = viewport;
  const screenMaxX = maxX * zoom + vpX;
  const screenCenterY = ((minY + maxY) / 2) * zoom + vpY;

  // 加号位置：右侧 +30px，垂直居中
  const handleX = screenMaxX + 30;
  const handleY = screenCenterY;

  const handleStyle = {
    position: 'fixed',
    left: handleX,
    top: handleY,
    transform: 'translate(-50%, -50%)',
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: isHovering || isDragging ? 'var(--bg-hover)' : 'var(--bg-panel)',
    border: `1.5px solid ${isHovering || isDragging ? 'var(--border-hover)' : 'var(--border-strong)'}`,
    display: (isDragging || isConnecting || hasMenu) ? 'none' : 'flex',  // 🔑 拖拽、连接中或有菜单时都隐藏加号
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'crosshair',
    zIndex: 1001,
    transition: 'all 0.1s ease',
    boxShadow: isHovering || isDragging ? '0 2px 6px rgba(0, 0, 0, 0.3)' : 'none',
    pointerEvents: 'all',
    userSelect: 'none',
    WebkitUserSelect: 'none',
  };

  const iconColor = isHovering || isDragging ? 'var(--text-hover)' : 'var(--text-secondary)';

  const handleMouseDown = (e) => {
    e.stopPropagation();
    setIsDragging(true);
    setDragPos({ x: e.clientX, y: e.clientY });

    // 调用父组件的回调，触发多选连接流程
    onMultiSelectConnectStart(e, validNodes.map(n => n.id));

    // 全局监听 mousemove 和 mouseup
    const handleMouseMove = (e) => {
      setDragPos({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = () => {
      // 松手时：清除拖拽状态和临时连线，EditorPage 层级会接管预览线渲染
      setIsDragging(false);
      setDragPos(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <>
      <div
        style={handleStyle}
        onMouseDown={handleMouseDown}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        title={`拖拽以连接选中的 ${validNodes.length} 个节点`}
        className="nodrag nopan"
      >
        <Plus size={12} color={iconColor} />
      </div>

      {/* 拖拽时显示临时连接线（仅拖拽过程中） */}
      {isDragging && dragPos && (
        <DraggingPreviewLines
          sourceNodeIds={validNodes.map(n => n.id)}
          targetMousePos={dragPos}
        />
      )}
    </>
  );
};

// 拖拽时的临时预览线组件（仅在拖拽中显示）
function DraggingPreviewLine({ nodeId, targetMousePos }) {
  const { flowToScreenPosition } = useReactFlow();
  useViewport();

  const startFlow = useStore((s) => {
    const node = s.nodeInternals.get(nodeId);
    if (!node) return null;
    const pos = node.positionAbsolute || node.position;
    const h = node[Symbol.for('internals')]?.handleBounds?.source?.[0];
    if (pos && h) return { x: pos.x + h.x + h.width / 2, y: pos.y + h.y + h.height / 2 };
    if (pos && node.width != null) return { x: pos.x + node.width, y: pos.y + (node.height || 0) / 2 };
    return null;
  });

  if (!startFlow || !targetMousePos.x || !targetMousePos.y) return null;

  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  const ox = vv ? vv.offsetLeft : 0;
  const oy = vv ? vv.offsetTop : 0;

  const s = flowToScreenPosition({ x: startFlow.x, y: startFlow.y });
  const sx = s.x + ox, sy = s.y + oy;
  const endX = targetMousePos.x + ox;
  const endY = targetMousePos.y + oy;
  const dx = Math.abs(endX - sx) * 0.5;

  return (
    <path
      d={`M ${sx},${sy} C ${sx + dx},${sy} ${endX - dx},${endY} ${endX},${endY}`}
      fill="none"
      stroke={C.EDGE_COLOR_MANUAL}
      strokeWidth={1.5}
      opacity={C.OPACITY_IDLE}
    />
  );
}

function DraggingPreviewLines({ sourceNodeIds, targetMousePos }) {
  if (!sourceNodeIds.length || !targetMousePos.x || !targetMousePos.y) return null;

  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  const ox = vv ? vv.offsetLeft : 0;
  const oy = vv ? vv.offsetTop : 0;

  const endX = targetMousePos.x + ox;
  const endY = targetMousePos.y + oy;

  return (
    <svg
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 998
      }}
    >
      {sourceNodeIds.map((nodeId) => (
        <DraggingPreviewLine
          key={nodeId}
          nodeId={nodeId}
          targetMousePos={targetMousePos}
        />
      ))}
      {/* 终点小绿点 */}
      <circle cx={endX} cy={endY} r={3} fill="#22c55e" />
    </svg>
  );
}

export default MultiSelectHandle;
